<template>
    <div class="h-full w-full border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-white dark:bg-slate-800/60 shadow-xs flex flex-col justify-between">
      <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{{ title }}</h3>
      
      <!-- Container wrapper with block display and full width -->
      <div class="w-full flex-1 min-h-[280px] block">
        <apexchart
          v-if="hasData"
          width="100%"
          height="100%"
          :type="chartType"
          :options="apexOptions"
          :series="apexSeries"
        />
        <div v-else class="h-full flex items-center justify-center text-xs text-slate-400">
          No chart data available
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { computed } from 'vue'
  import VueApexCharts from 'vue3-apexcharts'
  
  const apexchart = VueApexCharts
  
  const props = defineProps({
    title: String,
    value: Object
  })
  
  const hasData = computed(() => props.value && Array.isArray(props.value.labels) && props.value.labels.length > 0)
  
  const chartType = computed(() => {
    const t = (props.value?.chart || 'bar').toLowerCase()
    if (t === 'doughnut') return 'donut'
    return t
  })
  
  const isPieLike = computed(() => ['pie', 'donut'].includes(chartType.value))
  
  const apexSeries = computed(() => {
    if (!hasData.value) return []
    if (isPieLike.value) {
      return props.value.datasets?.[0]?.data || []
    }
    return (props.value.datasets || []).map(ds => ({
      name: ds.label || 'Series',
      data: ds.data || []
    }))
  })
  
  const apexOptions = computed(() => {
    if (!hasData.value) return {}
  
    const labels     = props.value.labels
    const rawDataset = props.value.datasets?.[0] || {}
  
    const customColors = isPieLike.value 
      ? rawDataset.backgroundColor 
      : props.value.datasets?.map(ds => ds.borderColor || ds.backgroundColor).filter(Boolean)
  
    const isDark = document.documentElement.classList.contains('dark')
  
    // Formatter for Indonesian/Standard thousand separator
    const formatNumber = (val) => {
      if (val === undefined || val === null) return ''
      return new Intl.NumberFormat('id-ID').format(val)
    }
  
    return {
        chart: {
            id                  : props.title?.replace(/\s+/g, '-').toLowerCase() || 'apex-chart',
            type                : chartType.value,
            background          : 'transparent',
            toolbar             : { show: !isPieLike.value },
            zoom                : { enabled: true },
            fontFamily          : 'inherit',
            redrawOnParentResize: true
        },
        theme: {
            mode: isDark ? 'dark' : 'light'
        },
        colors: customColors?.length ? customColors : ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'],
        labels: isPieLike.value ? labels : undefined,
      
          // 1. FORMAT TOOLTIP (Hover popup values)
        tooltip: {
            y: {
                formatter: (val) => formatNumber(val)
            }
        },
  
        // 2. FORMAT Y-AXIS (Left vertical numbers)
        yaxis: !isPieLike.value ? {
            labels: {
            style    : { colors: isDark ? '#94a3b8' : '#64748b' },
                formatter: (val) => formatNumber(val)
            }
        } : undefined,
  
        // 3. FORMAT DATALABELS (If numbers are shown directly on line/bar points)
        dataLabels: { 
            enabled  : chartType.value === 'line' ?  false : true,
            // formatter: (val) => formatNumber(val)
            formatter: (val, opts) => {
                if (isPieLike.value) {
                // Pilih salah satu:
                return `${val.toFixed(1)}%` // Menampilkan Persentase (misal: 23.0%)
                // return formatNumber(opts.w.globals.series[opts.seriesIndex]) // Jika ingin nilai angka asli
                }
                return formatNumber(val)
            },
            style: {
                fontSize: '12px',
                fontWeight: 'bold',
                colors: ['#ffffff'] // Warna teks di dalam slice
            },
            dropShadow: {
                enabled: true,
                top: 1,
                left: 1,
                blur: 1,
                opacity: 0.5
            }

        },
  
        xaxis: !isPieLike.value ? {
            categories: labels,
            labels    : {
                style: { colors: isDark ? '#94a3b8' : '#64748b' }
            }
        } : undefined,
        legend: {
            position: 'bottom',
            labels  : {
                colors: isDark ? '#cbd5e1' : '#334155'
            }
        },
        plotOptions: {
            pie: {
                expandOnClick: true,
                dataLabels: {
                    offset             : -10,
                    minAngleToShowLabel: 10,
                    external           : {
                        show: true,
                    },
                },
            },
        },
        stroke: {
            curve: 'smooth',
            width: chartType.value === 'line' ? 5 : 3,
        },
        markers: {
            size: 4,
            hover: {
              size: 6,
            },
        },
        grid: {
            borderColor: isDark ? '#334155' : '#e2e8f0'
        }
    }
})
  
</script>
  
<style scoped>
    /* Force inner ApexCharts SVG & canvas containers to expand 100% horizontally */
    :deep(.apexcharts-canvas),
    :deep(.apexcharts-svg),
    :deep(svg) {
        width: 100% !important;
    }
</style>